import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";

/**
 * Props for the ProtectedRoute component.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route protection component that requires authentication.
 * 
 * Features:
 * - Checks if user is authenticated before rendering children
 * - Subscribes to authentication state changes
 * - Shows loading state while checking authentication
 * - Redirects to /auth if user is not authenticated
 * 
 * This component wraps routes that require user authentication.
 * It ensures only logged-in users can access protected pages.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Effect: Sets up authentication state monitoring.
   * 
   * Process:
   * 1. Subscribes to auth state changes (sign in, sign out, token refresh)
   * 2. Checks for existing session immediately
   * 3. Updates user state when auth state changes
   * 
   * The listener is set up first to catch any state changes that might occur
   * during the getSession() call.
   */
  useEffect(() => {
    let isMounted = true;
    console.log('[ProtectedRoute] Setting up auth state monitoring');

    // Set up auth state listener FIRST to catch any changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[ProtectedRoute] Auth state changed:', event, 'User:', session?.user?.id || 'null');
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false); // CRITICAL: Set loading to false when auth state changes
        }
      }
    );

    // THEN check for existing session with error handling and timeout
    console.log('[ProtectedRoute] Checking for existing session...');
    
    // Add a timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('[ProtectedRoute] getSession() timeout after 5 seconds, using auth state change result');
        // Don't set loading here - let auth state change handle it
      }
    }, 5000);
    
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        clearTimeout(timeoutId);
        console.log('[ProtectedRoute] getSession result - Session:', session ? 'exists' : 'null', 'Error:', error?.message || 'none');
        if (!isMounted) {
          console.log('[ProtectedRoute] Component unmounted, ignoring getSession result');
          return;
        }
        
        if (error) {
          console.error('[ProtectedRoute] Error getting session:', error);
          
          // Capture session error to Sentry
          Sentry.captureException(error, {
            tags: {
              operation: 'session-check',
              errorType: 'authentication',
            },
            extra: {
              errorCode: (error as any).status || 'unknown',
              errorMessage: error.message,
              timestamp: new Date().toISOString(),
            },
          });
          
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
        // Only set loading to false if it's still true (auth state change might have already set it)
        setLoading((prev) => {
          if (prev) {
            console.log('[ProtectedRoute] Loading set to false after getSession');
          }
          return false;
        });
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error('[ProtectedRoute] Unexpected error getting session:', error);
        
        // Capture unexpected error to Sentry
        Sentry.captureException(error, {
          tags: {
            operation: 'session-check',
            errorType: 'unexpected',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
        
        if (isMounted) {
          setSession(null);
          setUser(null);
          setLoading((prev) => {
            if (prev) {
              console.log('[ProtectedRoute] Loading set to false after error');
            }
            return false;
          });
        }
      });

    return () => {
      console.log('[ProtectedRoute] Cleaning up auth state monitoring');
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  console.log('[ProtectedRoute] Render - Loading:', loading, 'User:', user?.id || 'null');

  if (loading) {
    console.log('[ProtectedRoute] Rendering loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  console.log('[ProtectedRoute] User authenticated, rendering children');
  return <>{children}</>;
};
